import { SearchIcon } from "lucide-react"
import { useEffect, useState } from "react"
import { NavLink, useLocation, useNavigate } from "react-router-dom"
import { Logo } from "@/components/Logo"
import { GuestMobileSidebar } from "@/components/mobile/GuestMobileSidebar"
import { Button } from "@/components/ui/button"
import { LoginModal } from "@/features/auth/components/LoginModal"
import { useTranslation } from "@/language"
import { getAssetUrl } from "@/utils/assets"

const GUEST_NAV_ITEMS = [
  { label: "My Rituality", path: "/my-rituality", requiresAuth: true } as const,
  { label: "LEARN AND PRACTICE", path: "/learn-and-practice" } as const,
  { label: "CONNECT", path: "/connect" } as const,
  { label: "BLOG", href: "https://myrituality.com/blog/" } as const,
]

export function GuestAppHeader() {
  const { t } = useTranslation("common")
  const navigate = useNavigate()
  const location = useLocation()
  const [isScrolled, setIsScrolled] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showLoginModal, setShowLoginModal] = useState(false)

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 25)
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const isActive = (path: string) => location.pathname.startsWith(path)
  const hamburger = getAssetUrl("hambuger_icon.svg")
  const logoWordmark = getAssetUrl("auth-logo.png")

  return (
    <>
      {/* Desktop header */}
      <header
        className={`absolute -top-1 left-0 right-0 z-50 transition-colors duration-200 hidden lg:block ${
          isScrolled ? "bg-black/20 backdrop-blur-md" : "bg-transparent"
        }`}
      >
        <div className="px-5 lg:px-17.5 py-5">
          <div className="relative h-full flex items-center justify-between py-5 lg:py-0">
            <div className="cursor-pointer" onClick={() => navigate("/learn-and-practice")}>
              <Logo />
            </div>

            <div className="flex-1 flex justify-center ms-20">
              <nav className="hidden lg:flex items-center gap-10" aria-label={t("header.guestNav")}>
                {GUEST_NAV_ITEMS.map(item =>
                  item.href ? (
                    <a
                      key={item.href}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative transition-colors text-[14px] font-normal leading-[20px] py-2 uppercase text-white"
                    >
                      {item.label}
                    </a>
                  ) : item.path ? (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={e => {
                        if ("requiresAuth" in item && item.requiresAuth) {
                          e.preventDefault()
                          setShowLoginModal(true)
                        }
                      }}
                      className={`relative transition-colors text-[14px] leading-[20px] py-2 uppercase ${
                        isActive(item.path) ? "font-semibold" : "font-normal"
                      }`}
                      aria-current={isActive(item.path) ? "page" : undefined}
                    >
                      <span className="text-white">{item.label}</span>
                    </NavLink>
                  ) : null,
                )}
              </nav>
            </div>

            <div className="flex items-center gap-3">
              {/* Search Button */}
              <Button
                variant="ghost"
                size="icon"
                aria-label={t("header.search")}
                className="rounded-lg"
                onClick={() => setShowLoginModal(true)}
              >
                <SearchIcon className="!w-[20px] !h-[20px] text-white" />
              </Button>
              <Button
                variant="primary"
                size="sm"
                className="rounded-full px-6 uppercase"
                onClick={() => setShowLoginModal(true)}
              >
                Sign In
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile top nav */}
      <header
        className={`sticky top-0 left-0 right-0 z-50 h-[48px] transition-all duration-300 lg:hidden ${
          isScrolled ? "bg-[#020202]/80 backdrop-blur-lg shadow-lg" : "bg-transparent"
        }`}
      >
        <div className="flex items-center justify-between px-5 h-full pt-[env(safe-area-inset-top)]">
          <div className="flex justify-center items-center gap-5">
            <button
              onClick={() => setSidebarOpen(true)}
              type="button"
              aria-label={t("header.openMenu")}
            >
              <img src={hamburger} className="flex items-center justify-center" alt="Menu" />
            </button>
            <div className="cursor-pointer" onClick={() => navigate("/learn-and-practice")}>
              <img
                src={logoWordmark}
                alt={t("notifications.brandName")}
                className="h-[21px] pb-1.25 cursor-pointer"
                onClick={() => navigate("/")}
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              aria-label={t("header.search")}
              className="rounded-lg"
              onClick={() => setShowLoginModal(true)}
            >
              <SearchIcon className="!w-[20px] !h-[20px] text-white" />
            </Button>
            <Button
              variant="primary"
              size="sm"
              className="rounded-full px-6 uppercase h-8 text-[12px]"
              onClick={() => setShowLoginModal(true)}
            >
              Sign In
            </Button>
          </div>
        </div>
      </header>

      <GuestMobileSidebar open={sidebarOpen} onOpenChange={setSidebarOpen} />
      <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} />
    </>
  )
}
